import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../services/api';
import { formatDate, formatDateLong } from '../dateUtils';

// TeamDirectory shows all therapists and lets you click through to see a richer profile.
// It filters by search term and optionally by "accepting new clients".
// Clicking on a profile opens a modal with birthday (day+month only), hire date,
// years at practice, license, specialties, education, bio, interests, and other details.
// The design uses subtle gradients, badges, and grid layouts for a polished look.
function TeamDirectory() {
  const [teamProfiles, setTeamProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAccepting, setFilterAccepting] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Format a date string as "MMM d" (e.g., "Sep 10"). Returns null if invalid.
  // Parses safely to avoid UTC-midnight timezone shift.
  const formatMonthDay = (dateStr) => {
    if (!dateStr) return null;
    const datePart = String(dateStr).split('T')[0];
    const parts = datePart.split('-').map(Number);
    if (parts.length < 3) return null;
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    if (isNaN(d)) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // Format a date string as "MMM d, yyyy". Returns null if invalid.
  // Uses formatDateLong from dateUtils for safe parsing.
  const formatFullDate = (dateStr) => formatDateLong(dateStr) || null;

  // Calculate the number of full years since the given date (hire anniversary). Returns null if invalid.
  const yearsSince = (dateStr, now = new Date()) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    let years = now.getFullYear() - d.getFullYear();
    const hadAnniv =
      now.getMonth() > d.getMonth() ||
      (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
    if (!hadAnniv) years -= 1;
    return Math.max(years, 0);
  };

  // Fetch team profiles on mount
  useEffect(() => {
    const fetchTeamProfiles = async () => {
      try {
        setLoading(true);
        const response = await API.get('/therapist/team-profiles');
        setTeamProfiles(response.data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching team profiles:', err);
        setError('Unable to load team profiles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchTeamProfiles();
  }, []);

  // Filter and search profiles with memoization
  const filteredProfiles = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    return (teamProfiles || []).filter((p) => {
      const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
      const searchMatch =
        !term ||
        fullName.includes(term) ||
        (p.specialties && p.specialties.toLowerCase().includes(term)) ||
        (p.license_type && p.license_type.toLowerCase().includes(term));
      const acceptingMatch = !filterAccepting || !!p.accepting_clients;
      return searchMatch && acceptingMatch;
    });
  }, [teamProfiles, searchTerm, filterAccepting]);

  // Open/close modal handlers
  const openProfileDetail = (profile) => {
    setSelectedProfile(profile);
    setShowDetailModal(true);
  };
  const closeProfileDetail = () => {
    setShowDetailModal(false);
    setSelectedProfile(null);
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={{ margin: 0 }}>Therapy Team Directory</h1>
        <Link to="/therapist/dashboard" style={backLinkStyle}>
          &larr; Back to Dashboard
        </Link>
      </div>

      <div style={searchFilterContainerStyle}>
        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search by name, specialty, or license..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={filterContainerStyle}>
          <label style={filterLabelStyle}>
            <input
              type="checkbox"
              checked={filterAccepting}
              onChange={() => setFilterAccepting(!filterAccepting)}
            />
            <span style={filterTextStyle}>Show only therapists accepting new clients</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div style={loadingStyle}>Loading team profiles...</div>
      ) : error ? (
        <div style={errorStyle}>{error}</div>
      ) : filteredProfiles.length === 0 ? (
        <div style={noResultsStyle}>No therapists match your search criteria.</div>
      ) : (
        <div style={profilesGridStyle}>
          {filteredProfiles.map((profile) => {
            const tenure = yearsSince(profile.hire_date);
            return (
              <div
                key={profile.therapist_id}
                style={profileCardStyle}
                onClick={() => openProfileDetail(profile)}
              >
                <div style={profileCardTopStyle}>
                  <div style={profilePhotoContainerStyle}>
                    {profile.photo_url ? (
                      <img
                        src={profile.photo_url}
                        alt={`${profile.first_name} ${profile.last_name}`}
                        style={profilePhotoStyle}
                      />
                    ) : (
                      <div
                        style={{
                          ...profilePhotoStyle,
                          backgroundColor: '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.3rem',
                          color: '#666',
                        }}
                      >
                        {(profile.first_name?.[0] || '') + (profile.last_name?.[0] || '')}
                      </div>
                    )}
                  </div>
                  <div style={profileCardHeaderStyle}>
                    <h3 style={profileNameStyle}>
                      {profile.first_name} {profile.last_name}
                    </h3>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {profile.license_type && (
                        <span style={licenseTypeStyle}>{profile.license_type}</span>
                      )}
                      {typeof tenure === 'number' && (
                        <span style={tenureBadgeStyle}>
                          {tenure === 0
                            ? 'First year'
                            : `${tenure} yr${tenure === 1 ? '' : 's'}`} at practice
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {profile.specialties && (
                  <p style={specialtiesStyle}>
                    <strong>Specialties:</strong> {profile.specialties}
                  </p>
                )}

                <div style={acceptingClientsContainerStyle}>
                  <div
                    style={{
                      ...acceptingClientsIndicatorStyle,
                      backgroundColor: profile.accepting_clients ? '#10B981' : '#EF4444',
                    }}
                  />
                  <span style={acceptingClientsTextStyle}>
                    {profile.accepting_clients
                      ? 'Accepting new clients'
                      : 'Not accepting new clients'}
                  </span>
                </div>

                <div style={viewProfileBtnContainerStyle}>
                  <button style={viewProfileBtnStyle}>View Profile</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Profile Detail Modal */}
      {showDetailModal && selectedProfile && (
        <div style={modalOverlayStyle} onClick={closeProfileDetail}>
          <div
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                {selectedProfile.first_name} {selectedProfile.last_name}
                {selectedProfile.license_type && (
                  <span style={modalLicenseBadgeStyle}>{selectedProfile.license_type}</span>
                )}
              </h2>
              <button
                onClick={closeProfileDetail}
                style={modalCloseButtonStyle}
              >
                &times;
              </button>
            </div>

            {/* decorative hero stripe */}
            <div style={modalHeroStyle} />

            <div style={modalBodyStyle}>
              <div style={modalProfileHeaderStyle}>
                <div style={modalPhotoContainerStyle}>
                  {selectedProfile.photo_url ? (
                    <img
                      src={selectedProfile.photo_url}
                      alt={`${selectedProfile.first_name} ${selectedProfile.last_name}`}
                      style={modalPhotoStyle}
                    />
                  ) : (
                    <div
                      style={{
                        ...modalPhotoStyle,
                        backgroundColor: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2rem',
                        color: '#666',
                      }}
                    >
                      {(selectedProfile.first_name?.[0] || '') +
                        (selectedProfile.last_name?.[0] || '')}
                    </div>
                  )}
                </div>

                <div style={modalProfileInfoStyle}>
                  <div style={acceptingClientsContainerStyle}>
                    <div
                      style={{
                        ...acceptingClientsIndicatorStyle,
                        width: 12,
                        height: 12,
                        backgroundColor: selectedProfile.accepting_clients
                          ? '#10B981'
                          : '#EF4444',
                      }}
                    />
                    <span
                      style={{
                        ...acceptingClientsTextStyle,
                        fontSize: '1rem',
                      }}
                    >
                      {selectedProfile.accepting_clients
                        ? 'Accepting new clients'
                        : 'Not accepting new clients'}
                    </span>
                  </div>

                  {/* Quick facts row: birthday, hire date, tenure */}
                  <div style={quickFactsRowStyle}>
                    <Fact
                      label="Birthday"
                      value={formatMonthDay(selectedProfile.dob) || '—'}
                    />
                    <Fact
                      label="Hire date"
                      value={formatFullDate(selectedProfile.hire_date) || '—'}
                    />
                    <Fact
                      label="Years at practice"
                      value={
                        yearsSince(selectedProfile.hire_date) ?? '—'
                      }
                    />
                  </div>

                  {/* Emergency contact info */}
                  <div style={{ marginTop: 12 }}>
                    <h4 style={{ margin: '0 0 6px', fontSize: '1rem' }}>Emergency Contact</h4>
                    <div style={emergencyBoxStyle}>
                      <div>
                        <strong>Name:</strong> {selectedProfile.emergency_contact_name || '—'}
                      </div>
                      <div>
                        <strong>Phone:</strong> {selectedProfile.emergency_contact_phone || '—'}
                      </div>
                    </div>
                  </div>

                  {/* Unavailable reason if applicable */}
                  {selectedProfile.unavailable_reason && !selectedProfile.accepting_clients && (
                    <div style={unavailableReasonStyle}>
                      <p style={{ margin: 0 }}>
                        <strong>Reason:</strong> {selectedProfile.unavailable_reason}
                      </p>
                    </div>
                  )}

                  {selectedProfile.license_number && (
                    <p style={detailItemStyle}>
                      <strong>License #:</strong> {selectedProfile.license_number}
                    </p>
                  )}
                </div>
              </div>

              <div style={modalDetailSectionStyle}>
                {selectedProfile.specialties && (
                  <Section title="Specialties">
                    {selectedProfile.specialties}
                  </Section>
                )}
                {selectedProfile.client_demographics && (
                  <Section title="Client Demographics">
                    {selectedProfile.client_demographics}
                  </Section>
                )}
                {selectedProfile.education && (
                  <Section title="Education">
                    {selectedProfile.education}
                  </Section>
                )}
                {selectedProfile.bio && (
                  <Section title="About">
                    {selectedProfile.bio}
                  </Section>
                )}
                {selectedProfile.interests && (
                  <Section title="Personal Interests">
                    {selectedProfile.interests}
                  </Section>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Presentational helper for detail sections
function Section({ title, children }) {
  return (
    <div style={modalSectionStyle}>
      <h3 style={sectionTitleStyle}>{title}</h3>
      <p style={{ marginTop: 8 }}>{children}</p>
    </div>
  );
}

// Presentational helper for quick facts cells
function Fact({ label, value }) {
  return (
    <div style={factStyle}>
      <div style={factLabelStyle}>{label}</div>
      <div style={factValueStyle}>{String(value)}</div>
    </div>
  );
}

/* ---------------- Styles ---------------- */
const containerStyle = {
  padding: '2rem',
  maxWidth: 1200,
  margin: '0 auto',
};
const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1.5rem',
};
const backLinkStyle = {
  textDecoration: 'none',
  color: '#2563EB',
  fontWeight: 600,
};
const searchFilterContainerStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '0.75rem',
  marginBottom: '1.25rem',
};
const searchContainerStyle = { flex: 1 };
const searchInputStyle = {
  width: '100%',
  padding: '0.7rem',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: '1rem',
};
const filterContainerStyle = { display: 'flex', alignItems: 'center' };
const filterLabelStyle = { display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 6 };
const filterTextStyle = { fontSize: '0.95rem' };

const loadingStyle = {
  textAlign: 'center',
  padding: '2rem',
  fontSize: '1.1rem',
  color: '#6b7280',
};
const errorStyle = {
  textAlign: 'center',
  padding: '1rem',
  fontSize: '1rem',
  color: '#b91c1c',
  background: '#fee2e2',
  borderRadius: 8,
};
const noResultsStyle = {
  textAlign: 'center',
  padding: '1.25rem',
  fontSize: '1rem',
  color: '#6b7280',
  background: '#f3f4f6',
  borderRadius: 8,
};

const profilesGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '1.25rem',
};
const profileCardStyle = {
  padding: '1rem',
  borderRadius: 12,
  boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
  border: '1px solid #e5e7eb',
  background: 'white',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform .15s ease, box-shadow .15s ease',
};
const profileCardTopStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.75rem',
};
const profilePhotoContainerStyle = { marginRight: '0.75rem' };
const profilePhotoStyle = {
  width: 56,
  height: 56,
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #2563EB',
};
const profileCardHeaderStyle = { flex: 1 };
const profileNameStyle = { margin: 0, marginBottom: 4, fontSize: '1.05rem', fontWeight: 700 };
const licenseTypeStyle = {
  display: 'inline-block',
  fontSize: '0.75rem',
  background: '#eef2ff',
  color: '#4338CA',
  padding: '2px 8px',
  borderRadius: 999,
};
const tenureBadgeStyle = {
  display: 'inline-block',
  fontSize: '0.75rem',
  background: '#e1fee7',
  color: '#0f766e',
  padding: '2px 8px',
  borderRadius: 999,
};
const specialtiesStyle = {
  margin: '0.35rem 0',
  fontSize: '0.9rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
};
const acceptingClientsContainerStyle = { display: 'flex', alignItems: 'center', marginTop: 6, gap: 8 };
const acceptingClientsIndicatorStyle = { width: 8, height: 8, borderRadius: '999px' };
const acceptingClientsTextStyle = { fontSize: '0.85rem', fontWeight: 600, color: '#374151' };
const viewProfileBtnContainerStyle = { marginTop: '0.75rem' };
const viewProfileBtnStyle = {
  background: '#C67A52',
  color: 'white',
  border: 'none',
  padding: '0.5rem 0.75rem',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '0.9rem',
  width: '100%',
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalContentStyle = {
  background: 'white',
  borderRadius: 12,
  width: 'min(940px, 94vw)',
  maxHeight: '92vh',
  overflow: 'auto',
  boxShadow: '0 12px 40px rgba(0,0,0,.25)',
};
const modalHeaderStyle = {
  padding: '1rem 1.25rem',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};
const modalHeroStyle = { height: 10, background: 'linear-gradient(90deg, #e0f2fe, #fce7f3)' };
const modalTitleStyle = { margin: 0, fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 10 };
const modalLicenseBadgeStyle = { fontSize: '0.8rem', background: '#eef2ff', color: '#4338CA', padding: '2px 8px', borderRadius: 999, fontWeight: 600 };
const modalCloseButtonStyle = { background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#6b7280' };

const modalBodyStyle = { padding: '1.25rem' };
const modalProfileHeaderStyle = { display: 'flex', gap: 16, marginBottom: '1.25rem', alignItems: 'center' };
const modalPhotoContainerStyle = {};
const modalPhotoStyle = { width: 112, height: 112, borderRadius: '50%', objectFit: 'cover', border: '3px solid #2563EB' };
const modalProfileInfoStyle = { flex: 1 };

const quickFactsRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 10 };
const factStyle = { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' };
const factLabelStyle = { fontSize: '0.75rem', color: '#6b7280' };
const factValueStyle = { fontSize: '0.95rem', fontWeight: 600, color: '#111827' };

// Emergency contact box styling
const emergencyBoxStyle = {
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 10,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 8,
};

const unavailableReasonStyle = { background: '#fff7ed', border: '1px solid #fed7aa', padding: 10, borderRadius: 8, marginTop: 8, fontSize: '0.9rem' };
const detailItemStyle = { margin: '0.4rem 0', fontSize: '0.95rem' };

const modalDetailSectionStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 };
const modalSectionStyle = { marginBottom: 8 };
const sectionTitleStyle = { borderBottom: '2px solid #2563EB', paddingBottom: 6, marginBottom: 8, fontSize: '1.05rem' };

export default TeamDirectory;